/**
 * Enrichment Service
 * 自动关联 UI 决策记录与券商交易数据
 */

/**
 * 转换券商日期格式 MM/DD/YYYY -> YYYY-MM-DD
 * @param {string} dateStr - 券商日期字符串 (e.g., "01/24/2026")
 * @returns {string} - 标准格式日期 (e.g., "2026-01-24")
 */
const normalizeBrokerDate = (dateStr) => {
  if (!dateStr) return '';

  // 如果已经是 YYYY-MM-DD 格式，直接返回
  if (dateStr.match(/^\d{4}-\d{2}-\d{2}$/)) {
    return dateStr;
  }

  // 转换 MM/DD/YYYY 格式
  const parts = dateStr.split('/');
  if (parts.length === 3) {
    const [month, day, year] = parts;
    return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
  }

  return dateStr;
};

/**
 * 匹配 UI 决策记录和券商交易数据
 * @param {Array} uiRecords - UI 决策记录
 * @param {Array} brokerTrades - 券商交易记录
 * @returns {Object} - 匹配结果
 */
export const matchRecords = (uiRecords, brokerTrades) => {
  const matches = [];
  const needConfirm = [];
  const unmatchedUI = [];
  const unmatchedBroker = [...brokerTrades];

  // 按日期分组（转换券商日期格式 MM/DD/YYYY -> YYYY-MM-DD）
  const brokerByDate = {};
  brokerTrades.forEach(trade => {
    const rawDate = trade.date || trade.Date || trade.tradeDate;
    const normalizedDate = normalizeBrokerDate(rawDate);
    if (!brokerByDate[normalizedDate]) {
      brokerByDate[normalizedDate] = [];
    }
    brokerByDate[normalizedDate].push(trade);
  });

  // 遍历 UI 记录，尝试匹配
  uiRecords.forEach(uiRecord => {
    // 跳过已关联的记录
    if (uiRecord.brokerRecordLinked) {
      return;
    }

    const candidates = findCandidates(uiRecord, brokerByDate[uiRecord.dateOnly] || []);

    if (candidates.length === 0) {
      // 未找到匹配
      unmatchedUI.push(uiRecord);
    } else if (candidates.length === 1) {
      // 唯一匹配
      const match = createMatch(uiRecord, candidates[0], 'high');
      matches.push(match);
      // 从未匹配列表中移除
      const index = unmatchedBroker.findIndex(b => b === candidates[0]);
      if (index > -1) {
        unmatchedBroker.splice(index, 1);
      }
    } else {
      // 多个候选，需要人工确认
      needConfirm.push({
        uiRecord,
        candidates,
        confidence: 'medium'
      });
    }
  });

  return {
    matches,
    needConfirm,
    unmatchedUI,
    unmatchedBroker
  };
};

/**
 * 查找候选的券商交易记录
 */
const findCandidates = (uiRecord, dayBrokerTrades) => {
  const uiAction = uiRecord.action.toLowerCase();
  const uiSymbol = uiRecord.symbol.toUpperCase();
  const uiQty = parseInt(uiRecord.quantity);
  const uiPrice = parseFloat(uiRecord.actualPrice);

  return dayBrokerTrades.filter(brokerTrade => {
    // 解析券商数据（支持多种格式）
    const brokerAction = (brokerTrade.Action || brokerTrade.action || brokerTrade.type || '').toLowerCase();
    const brokerSymbol = (brokerTrade.Symbol || brokerTrade.symbol || brokerTrade['Symbol / Description'] || '').toUpperCase().split(' ')[0];
    const brokerQty = parseInt(brokerTrade.Quantity || brokerTrade.quantity || brokerTrade.qty || 0);

    // 清理并解析价格（移除 $ 符号，处理 "000" 等无效值）
    let priceStr = (brokerTrade.Price || brokerTrade.price || '').toString().replace(/\$/g, '');
    const brokerPrice = parseFloat(priceStr);

    // 匹配条件
    const actionMatch = matchAction(uiAction, brokerAction);
    const symbolMatch = brokerSymbol === uiSymbol;
    const quantityMatch = Math.abs(brokerQty) === uiQty;

    // 价格匹配：如果券商价格无效（0 或 NaN），跳过价格检查，只匹配其他条件
    let priceMatch = true;
    if (brokerPrice && !isNaN(brokerPrice) && brokerPrice > 0) {
      priceMatch = Math.abs((brokerPrice - uiPrice) / uiPrice) < 0.02; // 2% 容错
    }

    return actionMatch && symbolMatch && quantityMatch && priceMatch;
  });
};

/**
 * 匹配动作（buy/sell vs entry/exit）
 */
const matchAction = (uiAction, brokerAction) => {
  if (uiAction === 'entry') {
    return brokerAction.includes('buy') || brokerAction.includes('bought');
  } else if (uiAction === 'exit') {
    return brokerAction.includes('sell') || brokerAction.includes('sold');
  }
  return false;
};

/**
 * 创建匹配记录
 */
const createMatch = (uiRecord, brokerTrade, confidence) => {
  // 清理并解析价格
  let priceStr = (brokerTrade.Price || brokerTrade.price || '').toString().replace(/\$/g, '');
  const brokerPrice = parseFloat(priceStr) || uiRecord.actualPrice; // 如果券商价格无效，使用 UI 价格

  const slippage = brokerPrice - uiRecord.actualPrice;

  // 清理并解析费用
  let feesStr = (brokerTrade['Fees & Comm'] || brokerTrade.fees || '0').toString().replace(/\$/g, '');
  const fees = parseFloat(feesStr) || 0;

  return {
    uiRecord,
    brokerTrade,
    confidence,
    brokerPrice,
    slippage,
    fees,
    matched: true
  };
};

/**
 * 计算配对交易的盈亏
 * @param {Array} matches - 已匹配的记录
 * @returns {Array} - 配对的交易及盈亏
 */
export const calculateProfitLoss = (matches) => {
  const pairs = [];
  const unmatchedEntries = [];
  const unmatchedExits = [];

  // 按股票分组
  const bySymbol = {};
  matches.forEach(match => {
    const symbol = match.uiRecord.symbol;
    if (!bySymbol[symbol]) {
      bySymbol[symbol] = { entries: [], exits: [] };
    }
    if (match.uiRecord.action === 'entry') {
      bySymbol[symbol].entries.push(match);
    } else {
      bySymbol[symbol].exits.push(match);
    }
  });

  // 配对 Entry 和 Exit
  Object.keys(bySymbol).forEach(symbol => {
    const { entries, exits } = bySymbol[symbol];

    // 按时间排序
    entries.sort((a, b) => new Date(a.uiRecord.timestamp) - new Date(b.uiRecord.timestamp));
    exits.sort((a, b) => new Date(a.uiRecord.timestamp) - new Date(b.uiRecord.timestamp));

    // FIFO 配对
    let entryIdx = 0;
    let exitIdx = 0;

    while (entryIdx < entries.length && exitIdx < exits.length) {
      const entry = entries[entryIdx];
      const exit = exits[exitIdx];

      const entryQty = entry.uiRecord.quantity;
      const exitQty = exit.uiRecord.quantity;

      if (entryQty === exitQty) {
        // 完整配对
        pairs.push({
          symbol,
          entry,
          exit,
          quantity: entryQty,
          entryPrice: entry.brokerPrice,
          exitPrice: exit.brokerPrice,
          profit: (exit.brokerPrice - entry.brokerPrice) * entryQty,
          profitPercent: ((exit.brokerPrice - entry.brokerPrice) / entry.brokerPrice) * 100,
          fees: (entry.fees || 0) + (exit.fees || 0),
          duration: calculateDuration(entry.uiRecord.timestamp, exit.uiRecord.timestamp)
        });
        entryIdx++;
        exitIdx++;
      } else if (entryQty < exitQty) {
        // Entry 数量较少，部分配对
        pairs.push({
          symbol,
          entry,
          exit: { ...exit, quantity: entryQty }, // 部分 exit
          quantity: entryQty,
          entryPrice: entry.brokerPrice,
          exitPrice: exit.brokerPrice,
          profit: (exit.brokerPrice - entry.brokerPrice) * entryQty,
          profitPercent: ((exit.brokerPrice - entry.brokerPrice) / entry.brokerPrice) * 100,
          fees: (entry.fees || 0),
          duration: calculateDuration(entry.uiRecord.timestamp, exit.uiRecord.timestamp),
          partial: true
        });
        entryIdx++;
        // Exit 还有剩余，继续匹配
      } else {
        // Exit 数量较少，部分配对
        pairs.push({
          symbol,
          entry: { ...entry, quantity: exitQty }, // 部分 entry
          exit,
          quantity: exitQty,
          entryPrice: entry.brokerPrice,
          exitPrice: exit.brokerPrice,
          profit: (exit.brokerPrice - entry.brokerPrice) * exitQty,
          profitPercent: ((exit.brokerPrice - entry.brokerPrice) / entry.brokerPrice) * 100,
          fees: (exit.fees || 0),
          duration: calculateDuration(entry.uiRecord.timestamp, exit.uiRecord.timestamp),
          partial: true
        });
        exitIdx++;
        // Entry 还有剩余，继续匹配
      }
    }

    // 未配对的 entries
    while (entryIdx < entries.length) {
      unmatchedEntries.push(entries[entryIdx]);
      entryIdx++;
    }

    // 未配对的 exits
    while (exitIdx < exits.length) {
      unmatchedExits.push(exits[exitIdx]);
      exitIdx++;
    }
  });

  return {
    pairs,
    unmatchedEntries,
    unmatchedExits
  };
};

/**
 * 计算持仓时长
 */
const calculateDuration = (entryTime, exitTime) => {
  const diff = new Date(exitTime) - new Date(entryTime);
  const minutes = Math.floor(diff / 60000);
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;

  if (hours > 0) {
    return `${hours}h ${mins}m`;
  } else {
    return `${mins}m`;
  }
};

/**
 * 分析 AI 推荐准确率
 * @param {Array} pairs - 配对的交易
 * @returns {Object} - AI 分析结果
 */
export const analyzeAIAccuracy = (pairs) => {
  const claudeStats = { total: 0, wins: 0, losses: 0, totalProfit: 0 };
  const gptStats = { total: 0, wins: 0, losses: 0, totalProfit: 0 };
  const bothAIStats = { total: 0, wins: 0, losses: 0, totalProfit: 0 };
  const noAIStats = { total: 0, wins: 0, losses: 0, totalProfit: 0 };

  pairs.forEach(pair => {
    const claudeRec = pair.entry.uiRecord.aiRecommendations?.claudeRecommended;
    const gptRec = pair.entry.uiRecord.aiRecommendations?.gptRecommended;
    const profit = pair.profit;
    const isWin = profit > 0;

    if (claudeRec && gptRec) {
      // 双 AI 推荐
      bothAIStats.total++;
      bothAIStats.totalProfit += profit;
      if (isWin) bothAIStats.wins++;
      else bothAIStats.losses++;
    }

    if (claudeRec) {
      claudeStats.total++;
      claudeStats.totalProfit += profit;
      if (isWin) claudeStats.wins++;
      else claudeStats.losses++;
    }

    if (gptRec) {
      gptStats.total++;
      gptStats.totalProfit += profit;
      if (isWin) gptStats.wins++;
      else gptStats.losses++;
    }

    if (!claudeRec && !gptRec) {
      noAIStats.total++;
      noAIStats.totalProfit += profit;
      if (isWin) noAIStats.wins++;
      else noAIStats.losses++;
    }
  });

  const calcWinRate = (stats) => stats.total > 0 ? (stats.wins / stats.total * 100).toFixed(1) : 0;
  const calcAvgProfit = (stats) => stats.total > 0 ? (stats.totalProfit / stats.total).toFixed(2) : 0;

  return {
    claude: {
      ...claudeStats,
      winRate: calcWinRate(claudeStats),
      avgProfit: calcAvgProfit(claudeStats)
    },
    gpt: {
      ...gptStats,
      winRate: calcWinRate(gptStats),
      avgProfit: calcAvgProfit(gptStats)
    },
    bothAI: {
      ...bothAIStats,
      winRate: calcWinRate(bothAIStats),
      avgProfit: calcAvgProfit(bothAIStats)
    },
    noAI: {
      ...noAIStats,
      winRate: calcWinRate(noAIStats),
      avgProfit: calcAvgProfit(noAIStats)
    }
  };
};

/**
 * 生成统计报告
 */
export const generateStats = (matches, pairs, unmatchedUI) => {
  const totalMatched = matches.length;
  const totalUnmatched = unmatchedUI.length;
  const totalPairs = pairs.length;
  const totalProfit = pairs.reduce((sum, p) => sum + p.profit, 0);
  const wins = pairs.filter(p => p.profit > 0).length;
  const losses = pairs.filter(p => p.profit <= 0).length;
  const winRate = totalPairs > 0 ? (wins / totalPairs * 100).toFixed(1) : 0;
  const avgProfit = totalPairs > 0 ? (totalProfit / totalPairs).toFixed(2) : 0;

  return {
    totalMatched,
    totalUnmatched,
    totalPairs,
    totalProfit: totalProfit.toFixed(2),
    wins,
    losses,
    winRate,
    avgProfit
  };
};
